import { z } from 'zod';
import {
  BIGQUERY_TYPES,
  DELETE_SCOPES,
  GROUP_EXECUTIONS,
  TEAM_TAGS,
} from './types';
import { buildTargetRef, TABLE_NAME_PLACEHOLDER } from './paths';
import {
  requiredIntervalTokens,
  scheduleGranularity,
  ScheduleGranularity,
} from './dates';

/**
 * 생성되는 파일들을 깨뜨리거나 **조용히 틀리게** 만드는 입력을 폼에서 거부한다.
 *
 * 새 규약(「DAG 작성 가이드」)의 실패 모드는 대부분 문법 오류가 아니라 **성공한
 * 채로 틀린 값이 들어가는 것**이다. 그래서 검사 대상이 "파이썬이 깨지는가" 에서
 * "날짜가 맞는가 / 범위가 맞는가 / 이름이 서로 같은가" 로 옮겨갔다. 가이드의
 * 「9. 빠른 체크리스트」가 요구하는 항목을 기계적으로 판정 가능한 것부터 전부
 * 여기로 끌어온다.
 */

const SNAKE_CASE = /^[a-z][a-z0-9_]*$/;

export const teamTagSchema = z.enum(TEAM_TAGS, {
  errorMap: () => ({
    message: `팀 폴더가 있는 팀만 선택할 수 있습니다 (${TEAM_TAGS.join(', ')}). 신규 팀은 #help_data 로 요청하세요`,
  }),
});

/**
 * `dags-{team}` 아래 폴더 경로. `/` 로 중첩한다.
 *
 * `conf`·`sql` 을 조각으로 쓸 수 없다 — 그 두 이름은 DAG 디렉터리 안의 하위
 * 폴더로 예약되어 있고, 같은 이름의 폴더가 섞이면 `template_searchpath` 와
 * `load_config` 가 무엇을 읽는지 사람이 추적할 수 없게 된다.
 */
const RESERVED_FOLDER_SEGMENTS = new Set(['conf', 'sql', 'utils', 'common']);

export const dagFolderSchema = z
  .string()
  .min(1, '폴더 경로를 입력하세요')
  .superRefine((value, ctx) => {
    const segments = value.split('/').map(s => s.trim()).filter(Boolean);
    if (!segments.length) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: '폴더 경로를 입력하세요' });
      return;
    }
    for (const segment of segments) {
      if (!SNAKE_CASE.test(segment)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `폴더 이름은 영문 소문자로 시작하고 소문자·숫자·밑줄만 쓸 수 있습니다: ${segment}`,
        });
      } else if (RESERVED_FOLDER_SEGMENTS.has(segment)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${segment} 은 DAG 디렉터리 안에서 예약된 이름입니다. 다른 폴더 이름을 쓰세요`,
        });
      }
    }
  });

/**
 * `dag_id` — Airflow 전역 식별자이자 `.py`·conf yaml 의 파일 stem.
 *
 * `{TEAM}_dag_` 접두사를 강제한다. 가이드의 규약이고, 이 접두사가 있어야
 * `.py` 가 DAG 이 import 하는 모듈(`utils`·`os`)을 가리는 일이 구조적으로
 * 불가능해진다.
 */
export const dagIdSchema = z.string().regex(
  /^[A-Z][A-Z0-9]*_dag_[a-z][a-z0-9_]*$/,
  'dag_id 는 {팀약자}_dag_{이름} 형태여야 합니다 (예: PA_dag_daily_order_summary). 이름 부분은 소문자·숫자·밑줄만 씁니다',
);

/** GCP project id. 소문자·숫자·하이픈, 6~30 자. */
export const projectIdSchema = z
  .string()
  .regex(
    /^[a-z][a-z0-9-]{4,28}[a-z0-9]$/,
    'BigQuery 프로젝트 id 형식이 아닙니다 (예: ygy-datawarehouse)',
  );

const bqIdentifier = (label: string) =>
  z
    .string()
    .regex(
      /^[A-Za-z_][A-Za-z0-9_]*$/,
      `${label}은 영문·숫자·밑줄만 쓸 수 있고 숫자로 시작할 수 없습니다`,
    );

export const targetSchema = z.object({
  project_id: projectIdSchema,
  data_set: bqIdentifier('데이터셋 이름'),
});

const CRON_FIELD_BOUNDS: [number, number][] = [
  [0, 59], // 분
  [0, 23], // 시
  [1, 31], // 일
  [1, 12], // 월
  [0, 7], // 요일 (7 = 일요일 허용)
];

function isValidCronField(field: string, [lo, hi]: [number, number]): boolean {
  return field.split(',').every(part => {
    const [range, stepRaw] = part.split('/');
    if (stepRaw !== undefined && !/^\d+$/.test(stepRaw)) return false;
    if (stepRaw !== undefined && Number(stepRaw) === 0) return false;
    if (range === '*') return true;
    const m = /^(\d+)(?:-(\d+))?$/.exec(range);
    if (!m) return false;
    const from = Number(m[1]);
    const to = m[2] === undefined ? from : Number(m[2]);
    return from >= lo && to <= hi && from <= to;
  });
}

/**
 * 5 필드 cron 만 받는다. **프리셋(`@daily`)과 `null` 은 거부한다** —
 * `.py` 가 `CronDataIntervalTimetable(dag_config["schedule"], ...)` 에 그대로
 * 넘기므로 cron 문자열이어야 하고, `dp-lakehouse` 의
 * `test_conf_yaml_is_airflow3_clean` 이 `schedule` 이 비면 PR 을 막는다.
 * (가이드 「5」 의 `schedule: null` 은 그 게이트와 어긋난다.)
 */
export const cronScheduleSchema = z.string().refine(
  value => {
    const fields = value.trim().split(/\s+/);
    if (fields.length !== 5) return false;
    return fields.every((f, i) => isValidCronField(f, CRON_FIELD_BOUNDS[i]));
  },
  {
    message:
      'cron 5 필드(분 시 일 월 요일)로 적어주세요 (예: 30 8 * * *). @daily 같은 프리셋은 쓸 수 없습니다',
  },
);

export const columnDefSchema = z.object({
  name: bqIdentifier('컬럼 이름'),
  type: z.enum(BIGQUERY_TYPES),
  required: z.boolean().optional(),
});

export const columnsSchema = z
  .array(columnDefSchema)
  .min(1, '컬럼을 최소 1 개 정의해야 합니다')
  .superRefine((columns, ctx) => {
    const seen = new Map<string, number>();
    columns.forEach((c, i) => {
      const first = seen.get(c.name.toLowerCase());
      if (first !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [i, 'name'],
          message: `컬럼 이름이 ${first + 1}번째와 중복됩니다`,
        });
      } else {
        seen.set(c.name.toLowerCase(), i);
      }
    });
  });

export const createTaskSchema = z
  .object({
    columns: columnsSchema,
    /** 빈 문자열이면 파티션 없는 테이블이다 — 작은 매핑·차원 테이블. */
    partition_by: z.string(),
  })
  .superRefine((task, ctx) => {
    if (task.partition_by && !task.columns.some(c => c.name === task.partition_by)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['partition_by'],
        message:
          '파티션 기준 컬럼이 위 컬럼 목록에 없습니다. 파티션은 나중에 바꿀 수 없으니 지금 정하세요',
      });
    }
  });

export const deleteTaskSchema = z
  .object({
    scope: z.enum(DELETE_SCOPES),
    date_column: z.string(),
    lookback_days: z
      .number()
      .int('며칠치는 정수여야 합니다')
      .min(1, '최소 1 일치여야 합니다')
      .max(31, '31 일을 넘기려면 Data Platform팀(@etl)에 문의하세요'),
  })
  .superRefine((task, ctx) => {
    // `scope: 'all'` 이면 날짜 컬럼이 쓰이지 않는다. 폼은 토글을 되돌릴 때를
    // 위해 값을 들고 있을 뿐이므로 그때는 검사하지 않는다.
    if (task.scope !== 'date') return;
    const parsed = bqIdentifier('기준 날짜 컬럼').safeParse(task.date_column);
    if (!parsed.success) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['date_column'],
        message: parsed.error.issues[0]?.message ?? '기준 날짜 컬럼을 입력하세요',
      });
    }
  });

// ---------------------------------------------------------------------------
// 사용자 SQL 검사 — 가이드가 ❌ 로 표시한 «조용히 틀리는» 형태를 거부한다.
// ---------------------------------------------------------------------------

/** 주석 안의 언급은 설명이므로 검사 대상이 아니다. */
function stripSqlComments(sql: string): string {
  return sql.replace(/\/\*[\s\S]*?\*\//g, ' ').replace(/--[^\n]*/g, ' ');
}

/**
 * `.in_timezone("Asia/Seoul")` 이 붙지 않은 `data_interval_*`.
 * `dp-lakehouse` 의 `_BARE_INTERVAL` 가드와 같은 불변식이다.
 */
const BARE_INTERVAL =
  /data_interval_(?:start|end)\b(?!\s*\.\s*in_timezone\(\s*['"]Asia\/Seoul['"]\s*\))/;

/** `data_interval_*.in_timezone("Asia/Seoul")` — 정상 변환. */
const KST_CONVERSION_G =
  /data_interval_(?:start|end)\s*\.\s*in_timezone\(\s*['"]Asia\/Seoul['"]\s*\)/g;

const ASIA_SEOUL_G = /Asia\/Seoul/g;

/** 공백 차이를 무시하고 대상 테이블 식을 찾는 정규식. */
function targetRefPattern(tableId: string): RegExp {
  const escaped = tableId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(
    `\`\\s*\\{\\{\\s*PROJECT_ID\\s*\\}\\}\\s*\\.\\s*\\{\\{\\s*DATA_SET\\s*\\}\\}\\s*\\.\\s*${escaped}\\s*\``,
  );
}

/** `INSERT INTO ... ( a, b, c )` 의 컬럼 목록. 파싱 실패하면 undefined. */
export function parseInsertColumns(sql: string): string[] | undefined {
  const m = /INSERT\s+INTO\s+[^(]*\(([^)]*)\)/i.exec(stripSqlComments(sql));
  if (!m) return undefined;
  const names = m[1]
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);
  if (!names.length || !names.every(n => /^[A-Za-z_][A-Za-z0-9_]*$/.test(n))) {
    return undefined;
  }
  return names;
}

/**
 * 안내의 세기.
 *
 * 기준은 **"막아야 하는가"** 가 아니라 **"막히는가"** 다 —
 *
 * - `error`: 생성물이 깨지거나 `dp-lakehouse` CI 게이트가 PR 을 막는 것.
 *   저장을 허용하면 사용자가 머지 불가인 PR 을 들고 CI 로그를 읽게 된다
 * - `warning`: 사람이 의도했을 수 있고 CI 도 막지 않는 것. 전체 적재를
 *   일부러 하는 경우가 있고, 그때 저장을 막으면 탈출구가 없다
 *
 * 경고는 저장을 막지 않지만 **PR 본문에 적힌다** — 리뷰어(@etl)가 그 선택을
 * 보고 판단할 수 있어야 한다.
 */
export type IssueSeverity = 'error' | 'warning';

export type SqlIssue = { severity: IssueSeverity; message: string };

/**
 * 「넣기」 SQL 검사. `granularity` 가 필요한 이유는 올바른 날짜 식이 주기에
 * 따라 달라지기 때문이다 — 일배치는 `start` 하나, 주·월배치는 `start`~`end`
 * 구간이다. 주배치에서 `start` 를 «어제» 로 착각하면 **성공한 채 0 건**이 된다.
 */
export function checkInsertSql(opts: {
  sql: string;
  granularity: ScheduleGranularity;
  /** 이 그룹의 대상 테이블. SQL 이 그 테이블을 가리키는지 본다. */
  tableId: string;
  /** 「생성」 task 의 컬럼 목록. 있으면 `INSERT INTO` 목록과 대조한다. */
  columns?: string[];
  /**
   * 날짜 토큰을 요구할지. 기본은 요구한다 — 날짜를 좁히지 않으면 재실행마다
   * 전체가 다시 들어간다. 다만 「지우기」 가 **테이블 전체**를 지우는 구성이면
   * 전체 적재가 의도이므로 요구하지 않는다.
   */
  requireIntervalTokens?: boolean;
}): SqlIssue[] {
  const { sql, granularity, tableId, columns } = opts;
  const requireIntervalTokens = opts.requireIntervalTokens ?? true;
  const body = stripSqlComments(sql);
  const issues: SqlIssue[] = [];

  if (!body.trim()) {
    return [{ severity: 'error', message: 'SQL 을 입력하세요' }];
  }

  if (/\bCURRENT_DATE\b/i.test(body)) {
    issues.push({
      // CI 가드는 없다. 다시 돌릴 일이 없는 SQL 이면 실제로 무해하므로 경고다.
      severity: 'warning',
      message:
        'CURRENT_DATE 는 지난 날짜를 다시 돌릴 때 엉뚱한 날짜가 들어갑니다 — {{ data_interval_start.in_timezone("Asia/Seoul") | ds }} 를 권장합니다',
    });
  }

  // 자리표시자가 남아 있으면 그게 원인이다 — 일반적인 «형태가 틀렸다» 메시지로
  // 뭉개면 무엇을 고쳐야 하는지 알 수 없다. 그대로 두면 Airflow 가 `TABLE_NAME`
  // 이라는 이름의 테이블을 찾다 실패한다.
  if (new RegExp(`\\b${TABLE_NAME_PLACEHOLDER}\\b`).test(body)) {
    issues.push({
      severity: 'error',
      message: `SQL 에 ${TABLE_NAME_PLACEHOLDER} 이 그대로 남아 있습니다 — 위 「테이블」 에 적은 이름${
        tableId ? `(${tableId})` : ''
      }으로 바꿔주세요`,
    });
  } else if (tableId && !targetRefPattern(tableId).test(body)) {
    issues.push({
      severity: 'error',
      message: `결과를 넣을 테이블은 ${buildTargetRef(tableId)} 형태로 두세요 — 프로젝트·데이터셋을 직접 적으면 설정과 어긋나도 아무도 모릅니다`,
    });
  }

  if (BARE_INTERVAL.test(body)) {
    issues.push({
      // dp-lakehouse 의 `_BARE_INTERVAL` 가드가 PR 을 막는다.
      severity: 'error',
      message:
        'data_interval_* 에 .in_timezone("Asia/Seoul") 이 빠졌습니다 — 오전 9 시 이전에 도는 DAG 에서 하루 이른 날짜가 들어갑니다',
    });
  }

  const converted = (body.match(KST_CONVERSION_G) ?? []).length;
  const mentions = (body.match(ASIA_SEOUL_G) ?? []).length;
  // `data_interval_*` 이 아예 없으면 이중 변환일 수가 없다. 이 조건이 없으면
  // `CURRENT_DATE('Asia/Seoul')` 처럼 interval 과 무관한 KST 언급이 오판된다.
  const hasInterval = /data_interval_(?:start|end)\b/.test(body);
  if (hasInterval && mentions > converted) {
    issues.push({
      // dp-lakehouse 의 SQL KST 가드가 PR 을 막는다.
      severity: 'error',
      message:
        "KST 변환이 두 번입니다 — {{ }} 안에서 이미 바꿨는데 SQL 에서 또 'Asia/Seoul' 을 붙이면 18 시간 어긋납니다",
    });
  }

  for (const token of requireIntervalTokens
    ? requiredIntervalTokens(granularity)
    : []) {
    if (!new RegExp(`data_interval_${token}\\b`).test(body)) {
      issues.push({
        // 전체를 매번 다시 넣는 것이 의도인 경우가 있다. 막으면 탈출구가 없다.
        severity: 'warning',
        message:
          granularity === 'daily'
            ? 'WHERE 절에 data_interval_start 가 없습니다 — 날짜를 좁히지 않으면 재실행할 때마다 전체가 다시 들어갑니다'
            : `${granularity === 'weekly' ? '주' : '월'}배치는 data_interval_${token} 을 권장합니다 — start 이상 end 미만 구간으로 쓰세요 (start 를 «어제» 로 쓰면 성공한 채 0 건이 됩니다)`,
      });
    }
  }

  if (
    granularity !== 'daily' &&
    /<=\s*DATE\(\s*'?\{\{\s*data_interval_end/i.test(body)
  ) {
    issues.push({
      severity: 'warning',
      message:
        '구간 끝은 <= 가 아니라 < (미만)을 권장합니다 — <= 로 쓰면 실행일 하루가 두 주기에 걸쳐 두 번 집계됩니다',
    });
  }

  if (columns?.length) {
    const parsed = parseInsertColumns(sql);
    // 파싱 실패는 거부 사유가 아니다 — INSERT 형태가 다양할 수 있고, 여기서
    // 막으면 탈출구가 없어진다. 읽을 수 있을 때만 대조한다.
    if (parsed && parsed.join(',') !== columns.join(',')) {
      issues.push({
        // 실행 시 BigQuery 가 분명한 메시지로 실패시킨다(조용하지 않다).
        severity: 'warning',
        message: `INSERT INTO 의 컬럼 목록이 테이블 생성 컬럼과 다릅니다 (이름·순서까지 같아야 합니다).\n  테이블: ${columns.join(', ')}\n  SQL:    ${parsed.join(', ')}`,
      });
    }
  }

  return issues;
}

/**
 * 한 그룹의 SQL 안내. 저장 차단(`superRefine`)과 경고 수집
 * (`collectPipelineWarnings`)이 **같은 함수**를 쓴다 — 두 벌로 두면 한쪽에만
 * 검사가 추가되는 결함군이 생긴다.
 */
function groupSqlIssues(
  config: {
    schedule: string;
    groups: {
      table_id: string;
      tasks: {
        create?: { columns: { name: string }[] };
        delete?: { scope: string };
        insert?: { sql: string };
      };
    }[];
  },
  index: number,
): SqlIssue[] {
  const group = config.groups[index];
  if (!group?.tasks.insert) return [];
  let granularity: ScheduleGranularity;
  try {
    granularity = scheduleGranularity(config.schedule);
  } catch {
    return [];
  }
  return checkInsertSql({
    sql: group.tasks.insert.sql,
    granularity,
    tableId: group.table_id,
    columns: group.tasks.create?.columns.map(c => c.name),
    requireIntervalTokens: group.tasks.delete?.scope !== 'all',
  });
}

export const pipelineTasksSchema = z.object({
  create: createTaskSchema.optional(),
  delete: deleteTaskSchema.optional(),
  insert: z.object({ sql: z.string() }).optional(),
});

/**
 * 원천 테이블. 테이블 이름 끝의 `*` 하나는 허용한다 — 날짜 샤드 테이블을
 * 와일드카드로 읽는 DAG 이 실제로 있다(`ods_billyo.{table}_20*`).
 */
export const sourceRefSchema = z.object({
  project_id: projectIdSchema,
  data_set: bqIdentifier('원천 데이터셋 이름'),
  table_id: z
    .string()
    .regex(
      /^[A-Za-z_][A-Za-z0-9_]*\*?$/,
      '원천 테이블 이름은 영문·숫자·밑줄만 쓸 수 있고, 끝에 * 하나만 붙일 수 있습니다',
    ),
});

export const tableGroupSchema = z.object({
  table_id: bqIdentifier('테이블 이름'),
  tasks: pipelineTasksSchema,
  // 이 필드가 생기기 전의 파이프라인에는 없다. 생성 폼은 빈 값으로 시작하므로
  // 새 파이프라인은 세 칸을 다 채워야 통과한다.
  source: sourceRefSchema.optional(),
});

export const pipelineConfigSchema = z
  .object({
    team_tag: teamTagSchema,
    dag_folder: dagFolderSchema,
    dag_id: dagIdSchema,
    // 선택값이다. 비면 렌더 전에 티켓 링크로 채운다(withDescriptionFallback).
    description: z.string(),
    schedule: cronScheduleSchema,
    target: targetSchema,
    groups: z.array(tableGroupSchema).min(1, '테이블 그룹을 최소 1 개 두세요'),
    group_execution: z.enum(GROUP_EXECUTIONS),
    tags: z.array(z.string().min(1)).min(1, '태그를 최소 1 개 입력하세요'),
    owner: z.string().min(1),
    owner_email: z.string().email(),
    start_date: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'start_date 는 YYYY-MM-DD 여야 합니다'),
    retries: z.number().int().min(0).max(5),
    retry_delay_minutes: z.number().int().min(1).max(60),
    execution_timeout_hours: z.number().min(0.25).max(12),
    gcp_conn_id: z.string().optional(),
  })
  .superRefine((config, ctx) => {
    if (!config.dag_id.startsWith(`${config.team_tag}_dag_`)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['dag_id'],
        message: `dag_id 는 ${config.team_tag}_dag_ 로 시작해야 합니다 (선택한 팀과 같아야 합니다)`,
      });
    }

    if (config.tags[0] !== config.team_tag) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['tags'],
        message: '태그의 첫 원소는 팀 약자여야 합니다 (팀별 검색이 이것에 의존합니다)',
      });
    }

    // 테이블이 겹치면 task id 와 SQL 파일명이 겹쳐 한쪽이 조용히 덮인다.
    const seen = new Map<string, number>();
    config.groups.forEach((group, i) => {
      const first = seen.get(group.table_id.toLowerCase());
      if (first !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['groups', i, 'table_id'],
          message: `${first + 1}번째 그룹과 같은 테이블입니다 — task id 와 SQL 파일명이 겹쳐 한쪽이 조용히 덮입니다`,
        });
      } else {
        seen.set(group.table_id.toLowerCase(), i);
      }
    });

    let granularity: ScheduleGranularity;
    try {
      granularity = scheduleGranularity(config.schedule);
    } catch {
      return; // cron 자체가 틀렸으면 위 스키마가 이미 보고했다
    }

    config.groups.forEach((group, i) => {
      const at = (...path: (string | number)[]) => ['groups', i, ...path];
      const { tasks } = group;

      if (!tasks.create && !tasks.delete && !tasks.insert) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: at('tasks'),
          message: '생성·지우기·넣기 중 최소 하나를 켜야 합니다',
        });
        return;
      }

      // 하루에 여러 번 도는 스케줄에서 **날짜 기준** 지우기는 같은 날짜를
      // 반복해 지우고 넣는다. 「전체」 지우기는 전체 재적재라 문제가 없다.
      if (tasks.delete?.scope === 'date' && granularity === 'hourly') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: at('tasks', 'delete'),
          message:
            '하루에 여러 번 도는 스케줄에는 날짜 기준 «지우기» 를 쓸 수 없습니다 — 같은 날짜를 반복해 지우고 넣게 됩니다. 「테이블 전체」 로 바꾸거나 Data Platform팀(@etl)에 문의하세요',
        });
      }

      // 「지우기」 만 켜면 데이터가 사라지기만 한다. 의도일 수도 있으나 사고일
      // 가능성이 훨씬 커서 막는다 — 정말 필요하면 @etl 경유다.
      if (tasks.delete && !tasks.insert) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: at('tasks', 'insert'),
          message:
            '«지우기» 만 켜면 매일 데이터를 지우기만 합니다. «넣기» 를 함께 켜세요',
        });
      }

      if (tasks.insert) {
        // **error 만** 저장을 막는다. 경고는 `collectPipelineWarnings` 가
        // 따로 모아 폼과 PR 본문에 보여준다.
        for (const issue of groupSqlIssues(config, i)) {
          if (issue.severity !== 'error') continue;
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: at('tasks', 'insert', 'sql'),
            message: issue.message,
          });
        }
      }
    });
  });

export type ValidationIssue = { path: string; message: string };

const toIssues = (error: z.ZodError): ValidationIssue[] =>
  error.issues.map(i => ({ path: i.path.join('.'), message: i.message }));

/** 폼에서 쓰기 좋은 형태로 검증 결과를 돌려준다. */
export function validatePipelineConfig(
  input: unknown,
): { ok: true } | { ok: false; issues: ValidationIssue[] } {
  const result = pipelineConfigSchema.safeParse(input);
  return result.success ? { ok: true } : { ok: false, issues: toIssues(result.error) };
}

/**
 * 저장을 막지 않는 안내들. 폼이 따로 보여주고 **PR 본문에도 적힌다** —
 * 리뷰어(@etl)가 그 선택을 보고 판단할 수 있어야 한다.
 *
 * 서버가 직접 계산한다. 클라이언트가 보내는 값이면 우회가 가능하고, 경고를
 * 숨긴 PR 이 올라올 수 있다.
 */
export function collectPipelineWarnings(config: {
  schedule: string;
  groups: {
    table_id: string;
    tasks: {
      create?: { columns: { name: string }[] };
      delete?: { scope: string };
      insert?: { sql: string };
    };
  }[];
}): ValidationIssue[] {
  return config.groups.flatMap((group, i) =>
    groupSqlIssues(config, i)
      .filter(issue => issue.severity === 'warning')
      .map(issue => ({
        path: `groups.${i}.tasks.insert.sql`,
        message: `${group.table_id || `테이블 ${i + 1}`}: ${issue.message}`,
      })),
  );
}

/** 「생성」 task 만 따로 검증 (폼 필드 단위). */
export function validateCreateTask(
  input: unknown,
): { ok: true } | { ok: false; issues: ValidationIssue[] } {
  const result = createTaskSchema.safeParse(input);
  return result.success ? { ok: true } : { ok: false, issues: toIssues(result.error) };
}
